import React, { useState, useEffect } from 'react';
import api from '../config/api';
import './Dashboard.css';

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

const HIDDEN = '•••';

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [visible, setVisible] = useState({
    revenue: false,
    monthlySalary: false,
    thisMonthRevenue: false,
    totalRevenue: false,
    financialMonthlySalary: false,
    monthlyRevenue: false,
  });

  const toggle = (key) => setVisible(prev => ({ ...prev, [key]: !prev[key] }));

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/api/dashboard/overview');
      setStats(response.data);
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="dashboard">
      <h1>Dashboard</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#3498db' }}>👥</div>
          <div className="stat-content">
            <h3>Employees</h3>
            <p className="stat-number">{stats?.employees?.total || 0}</p>
            <p className="stat-detail">Active: {stats?.employees?.active || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#2ecc71' }}>📁</div>
          <div className="stat-content">
            <h3>Projects</h3>
            <p className="stat-number">{stats?.projects?.total || 0}</p>
            <p className="stat-detail">Active: {stats?.projects?.active || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#9b59b6' }}>📄</div>
          <div className="stat-content">
            <h3>Invoices</h3>
            <p className="stat-number">{stats?.invoices?.total || 0}</p>
            <p className="stat-detail">Paid: {stats?.invoices?.paid || 0}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f39c12' }}>💰</div>
          <div className="stat-content">
            <div className="stat-header">
              <h3>Revenue</h3>
              <button className="eye-btn" onClick={() => toggle('revenue')} title={visible.revenue ? 'Hide' : 'Show'}>
                {visible.revenue ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            </div>
            <p className="stat-number">
              {visible.revenue ? `$${stats?.invoices?.revenue?.toFixed(2) || '0.00'}` : HIDDEN}
            </p>
            <p className="stat-detail">Total Earned</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#1abc9c' }}>📅</div>
          <div className="stat-content">
            <div className="stat-header">
              <h3>This Month's Revenue</h3>
              <button className="eye-btn" onClick={() => toggle('thisMonthRevenue')} title={visible.thisMonthRevenue ? 'Hide' : 'Show'}>
                {visible.thisMonthRevenue ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            </div>
            <p className="stat-number">
              {visible.thisMonthRevenue ? `$${stats?.invoices?.monthlyRevenue?.toFixed(2) || '0.00'}` : HIDDEN}
            </p>
            <p className="stat-detail">Current Month</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#34495e' }}>💵</div>
          <div className="stat-content">
            <div className="stat-header">
              <h3>Monthly Salary</h3>
              <button className="eye-btn" onClick={() => toggle('monthlySalary')} title={visible.monthlySalary ? 'Hide' : 'Show'}>
                {visible.monthlySalary ? <EyeIcon /> : <EyeOffIcon />}
              </button>
            </div>
            <p className="stat-number">
              {visible.monthlySalary ? `PKR ${stats?.salaries?.currentMonth?.toFixed(2) || '0.00'}` : HIDDEN}
            </p>
            <p className="stat-detail">Current Month</p>
          </div>
        </div>
      </div>

      <div className="dashboard-summary">
        <div className="card">
          <h2>Financial Summary</h2>
          <div className="summary-items">
            <div className="summary-item">
              <span className="summary-label">
                Total Revenue
                <button className="eye-btn eye-btn-inline" onClick={() => toggle('totalRevenue')} title={visible.totalRevenue ? 'Hide' : 'Show'}>
                  {visible.totalRevenue ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </span>
              <span className="summary-value success">
                {visible.totalRevenue ? `$${stats?.invoices?.revenue?.toFixed(2) || '0.00'}` : HIDDEN}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">
                Monthly Revenue
                <button className="eye-btn eye-btn-inline" onClick={() => toggle('monthlyRevenue')} title={visible.monthlyRevenue ? 'Hide' : 'Show'}>
                  {visible.monthlyRevenue ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </span>
              <span className="summary-value success">
                {visible.monthlyRevenue ? `$${stats?.invoices?.monthlyRevenue?.toFixed(2) || '0.00'}` : HIDDEN}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">
                Monthly Salary
                <button className="eye-btn eye-btn-inline" onClick={() => toggle('financialMonthlySalary')} title={visible.financialMonthlySalary ? 'Hide' : 'Show'}>
                  {visible.financialMonthlySalary ? <EyeIcon /> : <EyeOffIcon />}
                </button>
              </span>
              <span className="summary-value">
                {visible.financialMonthlySalary ? `PKR ${stats?.salaries?.currentMonth?.toFixed(2) || '0.00'}` : HIDDEN}
              </span>
            </div>
          </div>
        </div>

        <div className="card">
          <h2>Quick Stats</h2>
          <div className="summary-items">
            <div className="summary-item">
              <span>Pending Invoices:</span>
              <span className="summary-value warning">{stats?.invoices?.pending || 0}</span>
            </div>
            <div className="summary-item">
              <span>Active Employees:</span>
              <span className="summary-value">{stats?.employees?.active || 0}</span>
            </div>
            <div className="summary-item">
              <span>Active Projects:</span>
              <span className="summary-value">{stats?.projects?.active || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
